import { useId, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';
import { IconAlert } from './icons';

type Control = { id: string; describedBy: string | undefined; invalid: boolean; required: boolean };

/**
 * Label + control + hint + error, wired for screen readers: the label targets the control, hint and error are in
 * aria-describedby, and the error is announced (role="alert"). Pass required to the control: the native attribute
 * announces it, the asterisk is visual only. Render-prop children receive the wiring.
 */
export function Field({ label, hint, error, required, id: idProp, className, children }: {
  label: ReactNode; hint?: ReactNode; error?: ReactNode; required?: boolean; id?: string; className?: string;
  children: (c: Control) => ReactNode;
}) {
  const auto = useId();
  const id = idProp ?? `qk${auto.replace(/:/g, '')}`;
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('qk-field', className)}>
      <label className="qk-field__label" htmlFor={id}>
        {label}
        {required ? <span className="qk-field__req" aria-hidden="true">*</span> : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error), required: Boolean(required) })}
      {hint ? <p className="qk-field__hint" id={`${id}-hint`}>{hint}</p> : null}
      {error ? <p className="qk-field__error" id={`${id}-err`} role="alert"><IconAlert width={14} height={14} />{error}</p> : null}
    </div>
  );
}

export type KitInputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; icon?: ReactNode; ref?: Ref<HTMLInputElement> };

export function Input({ invalid, icon, className, ...rest }: KitInputProps) {
  const input = <input {...rest} aria-invalid={invalid || rest['aria-invalid'] || undefined} className={cx('qk-input', className)} />;
  if (!icon) return input;
  return <div className="qk-input-wrap"><span className="qk-input-wrap__icon">{icon}</span>{input}</div>;
}

export function Textarea({ invalid, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean; ref?: Ref<HTMLTextAreaElement> }) {
  return <textarea {...rest} aria-invalid={invalid || undefined} className={cx('qk-input', className)} />;
}

export type SelectOption = { value: string; label: string; disabled?: boolean };

/** Native select: best keyboard, screen-reader and mobile behaviour, styled to match the inputs. */
export function Select({ invalid, options, placeholder, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & {
  invalid?: boolean; options?: SelectOption[]; placeholder?: string; ref?: Ref<HTMLSelectElement>;
}) {
  return (
    <select {...rest} aria-invalid={invalid || undefined} className={cx('qk-select', className)}>
      {placeholder ? <option value="" disabled>{placeholder}</option> : null}
      {options?.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      {children}
    </select>
  );
}
